// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getPurchaseOrderURLLinks } from '@/purchase-order/api/get-purchase-order-url-links';
import { purchaseOrderURLLinksQueryKey } from '@/purchase-order/query-keys';
import { useQuery } from '@tanstack/react-query';

interface UsePurchaseOrderURLLinksOptions {
  /** The key of the purchase order to fetch URL links for */
  purchaseOrderKey: string;
}

/**
 * Hook to fetch URL links associated with a purchase order.
 * Uses a regular query (not suspense) to allow graceful error handling
 * without crashing the entire page.
 *
 * @param options - Options including the purchase order key
 * @returns The URL links, loading state, error state, and query methods
 */
export function usePurchaseOrderURLLinks(
  options: UsePurchaseOrderURLLinksOptions
) {
  const { data, isLoading, isError, error, refetch, ...query } = useQuery({
    queryKey: purchaseOrderURLLinksQueryKey(options.purchaseOrderKey),
    queryFn: () => getPurchaseOrderURLLinks(options.purchaseOrderKey),
  });

  return {
    urlLinks: data?.data ?? [],
    isLoading,
    isError,
    error,
    refetch,
    ...query,
  };
}
